import React, { useState, useRef, useEffect } from "react";

interface Language {
  LangId: number;
  Name: string;
}

interface LanguageMultiSelectProps {
  languages: Language[];
  selectedLanguages: string;
  onChange: (languages: string) => void;
  disabled?: boolean;
}

const LanguageMultiSelect: React.FC<LanguageMultiSelectProps> = ({
  languages,
  selectedLanguages,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedArray = selectedLanguages
    ? selectedLanguages.split(",").map((l) => l.trim()).filter(Boolean)
    : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (langName: string) => {
    let newSelected: string[];

    if (selectedArray.includes(langName)) {
      // Remove language
      newSelected = selectedArray.filter((l) => l !== langName);
    } else {
      // Add language
      newSelected = [...selectedArray, langName];
    }

    // Convert array back to comma-separated string
    onChange(newSelected.join(", "));
  };

  const handleSelectAll = () => {
    if (selectedArray.length === languages.length) {
      // Deselect all
      onChange("");
    } else {
      // Select all
      onChange(languages.map((l) => l.Name).join(", "));
    }
  };

  const isChecked = (langName: string) => selectedArray.includes(langName);
  const allSelected = selectedArray.length === languages.length && languages.length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Display Input */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-1 text-xs border border-1 rounded-sm transition-all cursor-pointer ${
          disabled
            ? "bg-gray-100 cursor-not-allowed"
            : "bg-white hover:border-blue-400"
        } ${isOpen ? "border-blue-500 ring-1 ring-blue-200" : "border-gray-300"}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {selectedArray.length === 0 ? (
              <span className="text-gray-400">Select languages...</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedArray.map((lang, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1  bg-blue-100 text-blue-700 rounded text-xs font-medium"
                  >
                    {lang}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(lang);
                        }}
                        className="hover:text-blue-900"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border-1 border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {/* Select All Option */}
          <div
            className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex items-center gap-3 border-b border-gray-200 bg-gray-50"
            onClick={handleSelectAll}
          >
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <span className="text-xs font-semibold text-gray-700">
              {allSelected ? "Deselect All" : "Select All"}
            </span>
          </div>

          {/* Language Options - Vertical List */}
          {languages.map((lang) => (
            <div
              key={lang.LangId}
              className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex items-center gap-3 transition-colors"
              onClick={() => handleToggle(lang.Name)}
            >
              <input
                type="checkbox"
                checked={isChecked(lang.Name)}
                onChange={() => handleToggle(lang.Name)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-xs text-gray-700">{lang.Name}</span>
            </div>
          ))}

          {languages.length === 0 && (
            <div className="px-4 py-3 text-xs text-gray-500 text-center">
              No languages available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageMultiSelect;
